import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Box, Button, EmptyState, Flex, LoadingOrbit, Page, PageHeader, Select, Text, TextField, useToast, type SelectOption } from '@chatsdet/ui';
import type { AttachmentKind } from '@chatsdet/types';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { AttachmentCard } from '../../features/attachments/components/AttachmentCard';
import { AttachmentUploadDialog } from '../../features/attachments/components/AttachmentUploadDialog';
import {
  useAttachmentList,
  useDeleteAttachment,
  useUploadAttachments,
} from '../../features/attachments/api';
import { CloudArrowUp, Paperclip } from '@phosphor-icons/react';

type AttachmentsSearchParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  kind?: AttachmentKind;
};

type AttachmentsRoutePath = '/attachments' | '/test-data/attachments';

const kindOptions: SelectOption[] = [
  { id: 'all', label: 'All types' },
  { id: 'image', label: 'Images' },
  { id: 'pdf', label: 'PDFs' },
  { id: 'spreadsheet', label: 'Spreadsheets' },
  { id: 'document', label: 'Documents' },
];

export function Attachments({ showBreadcrumbs = true }: { showBreadcrumbs?: boolean } = {}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const routePath: AttachmentsRoutePath = showBreadcrumbs
    ? '/attachments'
    : '/test-data/attachments';
  const canDeleteAttachments = user?.isSuperadmin === true || user?.role === 'admin';

  const breadcrumbs = React.useMemo(
    () =>
      showBreadcrumbs
        ? [
            { label: 'Home', onPress: () => navigate({ to: '/' }) },
            { label: 'Attachments', onPress: () => navigate({ to: routePath }) },
          ]
        : undefined,
    [navigate, routePath, showBreadcrumbs]
  );
  useBreadcrumbs(breadcrumbs);

  const searchParams = useSearch({ strict: false }) as AttachmentsSearchParams;
  const page = searchParams.page ?? 1;
  const pageSize = searchParams.pageSize ?? 24;
  const kind = (searchParams.kind ?? 'all') as AttachmentKind;
  const search = searchParams.search ?? '';
  const [searchInput, setSearchInput] = React.useState(search);

  React.useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const updateSearchParams = React.useCallback(
    (updates: Partial<AttachmentsSearchParams>, resetPage = false) => {
      const prev = searchParams;
      const next: AttachmentsSearchParams = { ...prev, ...updates };
      if (resetPage && !('page' in updates)) {
        next.page = 1;
      }

      const cleaned: AttachmentsSearchParams = {};
      if (next.page && next.page !== 1) cleaned.page = next.page;
      if (next.pageSize && next.pageSize !== 24) cleaned.pageSize = next.pageSize;
      if (next.search) cleaned.search = next.search;
      if (next.kind && next.kind !== 'all') cleaned.kind = next.kind;

      navigate({ to: routePath, search: cleaned, replace: true });
    },
    [navigate, routePath, searchParams]
  );

  const listQuery = useAttachmentList({
    page,
    pageSize,
    search,
    kind,
  });

  const upload = useUploadAttachments();
  const del = useDeleteAttachment();

  const handleUpload = React.useCallback(
    async (files: File[]) => {
      try {
        await upload.mutateAsync(files);
        toast.success(`${files.length} attachment${files.length === 1 ? '' : 's'} uploaded.`);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Unable to upload attachments.');
      }
    },
    [toast, upload]
  );

  const handleDelete = React.useCallback(
    async (id: string) => {
      const confirmed = window.confirm('Delete this attachment?');
      if (!confirmed) return;
      try {
        await del.mutateAsync(id);
        toast.success('Attachment deleted.');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Unable to delete attachment.');
      }
    },
    [del, toast]
  );

  const items = listQuery.data?.data ?? [];
  const pagination = listQuery.data?.pagination ?? null;

  const handleEmptyDrop = React.useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (upload.isPending) return;
      const files = Array.from(event.dataTransfer.files ?? []);
      if (files.length === 0) return;
      await handleUpload(files);
    },
    [handleUpload, upload.isPending]
  );

  const uploadAction = (
    <AttachmentUploadDialog
      isUploading={upload.isPending}
      onUpload={handleUpload}
      triggerLabel="Upload"
      trigger={
        <Button
          variant="primary"
          iconLeft={<CloudArrowUp size={16} />}
          aria-label="Upload attachment"
        >
          Upload
        </Button>
      }
    />
  );

  const content = (
    <>
      <Flex align="end" gap="sm" className="mb-4">
        <Box className="flex-1">
          <TextField
            label="Search"
            placeholder="Search filenames…"
            value={searchInput}
            onChange={setSearchInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                updateSearchParams({ search: searchInput.trim() }, true);
              }
            }}
          />
        </Box>
        <Box className="w-[200px]">
          <Select
            label="Type"
            aria-label="Attachment type"
            items={kindOptions}
            selectedKey={kind}
            onSelectionChange={(key) =>
              updateSearchParams({ kind: String(key) as AttachmentKind }, true)
            }
          />
        </Box>
        <Box>
          <Button
            variant="primary"
            onPress={() => updateSearchParams({ search: searchInput.trim() }, true)}
          >
            Apply
          </Button>
        </Box>
      </Flex>

      {listQuery.isLoading && items.length === 0 ? (
        <Flex justify="center" className="py-12">
          <LoadingOrbit />
        </Flex>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Paperclip size={24} />}
          title="No attachments yet"
          description="Drag and drop files here, or upload your first attachment."
          action={
            <AttachmentUploadDialog
              isUploading={upload.isPending}
              onUpload={handleUpload}
              triggerLabel="Upload your first attachment"
            />
          }
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleEmptyDrop}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((item) => (
            <AttachmentCard
              key={item.id}
              attachment={item}
              onDelete={canDeleteAttachments ? handleDelete : undefined}
            />
          ))}
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <Flex align="center" justify="between" className="mt-6">
          <Button
            variant="secondary"
            isDisabled={page <= 1 || listQuery.isFetching}
            onPress={() => updateSearchParams({ page: Math.max(1, page - 1) })}
          >
            Previous
          </Button>
          <Text size="sm" className="text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages}
          </Text>
          <Button
            variant="secondary"
            isDisabled={page >= pagination.totalPages || listQuery.isFetching}
            onPress={() => updateSearchParams({ page: Math.min(pagination.totalPages, page + 1) })}
          >
            Next
          </Button>
        </Flex>
      )}
    </>
  );

  if (!showBreadcrumbs) {
    return (
      <Box className="space-y-6">
        <PageHeader
          size="section"
          title="Attachments"
          description="Upload reusable images and PDFs for your workflows."
          actions={uploadAction}
        />
        {content}
      </Box>
    );
  }

  return (
    <Page>
      <PageHeader
        title="Attachments"
        description="Upload reusable images and PDFs for your workflows."
        actions={uploadAction}
      />
      {content}
    </Page>
  );
}
